import "../styles/style.css";
import "font-awesome/css/font-awesome.min.css";
import emptyImage from "../images/empty.jpg";
import { useState, useContext, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ChatContext } from "../context/contexts";
import { getTimeAgo } from "../util/helperFunctions";

function MessageHead(props) {
  const Chat = useContext(ChatContext);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const moreMenuRef = useRef(null);
  const navigate = useNavigate();

  const {
    conversationData,
    setMobileChatDetailShow,
    showHideSearchmodal,
    showHideViewMembersModal,
    showHideViewConversationFilesModal,
    conversationUserPermission,
    showHideEditConversationModal,
    showHideViewActivityLogsModal,
    userDetails,
  } = props;

  const conversation = conversationData?.conversation;
  const members =
    conversation?.members || Chat.conversationMembersData || [];
  const onlineUsers = Chat.onlineUsers || [];
  const currentUserId = userDetails?.id;
  const isSelfAdmin = conversationUserPermission?.is_admin || false;
  const isGroup = !!conversation?.is_group;

  let conversationImage;
  if (conversationData) {
    const FINAL_RESOURCES_URL = `${process.env.REACT_APP_IMAGES_DATA_RESOURCES_URL}/${conversation?.image}`;
    conversationImage =
      conversation?.image &&
      conversation?.image?.includes("empty-filler-image.jpg")
        ? emptyImage
        : conversation?.image !== "" && conversation?.image !== null
          ? FINAL_RESOURCES_URL
          : emptyImage;
  } else {
    conversationImage = emptyImage;
  }

  const { isActive, statusText } = useMemo(() => {
    if (!conversationData || !conversation) {
      return { isActive: false, statusText: "" };
    }

    const otherMembers = members.filter((member) => {
      const memberId = member?.user?.id ?? member?.user_id;
      return memberId != null && memberId !== currentUserId;
    });

    if (isGroup) {
      const memberCount = members.length;
      const activeCount = otherMembers.filter((member) => {
        const memberId = member?.user?.id ?? member?.user_id;
        const onlineData = onlineUsers.find(
          (onlineUser) => onlineUser.user_id === memberId,
        );
        return onlineData?.is_online;
      }).length;

      if (activeCount > 0) {
        return {
          isActive: true,
          statusText: `${memberCount} members · ${activeCount} active`,
        };
      }
      return {
        isActive: false,
        statusText: `${memberCount} member${memberCount === 1 ? "" : "s"}`,
      };
    }

    const otherMember = otherMembers[0];
    if (!otherMember) {
      return { isActive: false, statusText: "Offline" };
    }

    const otherUserId = otherMember?.user?.id ?? otherMember?.user_id;
    const onlineData = onlineUsers.find(
      (onlineUser) => onlineUser.user_id === otherUserId,
    );

    if (onlineData?.is_online) {
      return { isActive: true, statusText: "Active now" };
    }

    const lastOnline = onlineData?.last_online;
    if (lastOnline) {
      const ago = getTimeAgo(lastOnline);
      return {
        isActive: false,
        statusText: ago ? `Active ${ago} ago` : "Offline",
      };
    }

    return { isActive: false, statusText: "Offline" };
  }, [
    conversationData,
    conversation,
    members,
    onlineUsers,
    currentUserId,
    isGroup,
  ]);

  useEffect(() => {
    if (!isMoreMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (
        moreMenuRef.current &&
        !moreMenuRef.current.contains(event.target)
      ) {
        setIsMoreMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsMoreMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMoreMenuOpen]);

  const toggleDropdown = () => {
    setIsMoreMenuOpen((open) => !open);
  };

  const closeMenu = () => setIsMoreMenuOpen(false);

  const handleBackEvent = () => {
    setMobileChatDetailShow(false);
    navigate("/");
  };

  const toggleSearchmodal = () => {
    closeMenu();
    if (conversationData) showHideSearchmodal();
  };

  const toggleEditConversationModal = () => {
    closeMenu();
    if (conversationData) showHideEditConversationModal();
  };

  const toggleViewMembersModal = () => {
    closeMenu();
    if (conversationData) showHideViewMembersModal();
  };

  const toggleViewConversationFilesModal = () => {
    closeMenu();
    if (conversationData) showHideViewConversationFilesModal();
  };

  const toggleViewActivityLogModal = () => {
    closeMenu();
    if (conversationData) showHideViewActivityLogsModal();
  };

  const conversationName = conversation?.name || "";

  return (
    <div className="message__head-inner">
      <button
        type="button"
        className="message__back-btn"
        aria-label="Go back"
        onClick={handleBackEvent}
      >
        <i
          className="fa fa-arrow-left message__back-btn-icon"
          aria-hidden="true"
        ></i>
      </button>

      <button
        type="button"
        className="message__head-identity"
        onClick={toggleViewMembersModal}
        disabled={!conversationData}
        aria-label={
          conversationName
            ? `View members of ${conversationName}`
            : "View members"
        }
      >
        <span className="message__head-avatar-wrap">
          <img
            src={conversationImage}
            alt=""
            className="message__head-img"
          />
          {conversationData && (
            <span
              className={`message__head-status-badge ${
                isActive ? "is-online" : "is-offline"
              }`}
              aria-hidden="true"
            />
          )}
        </span>
        <span className="message__head-details">
          <span className="message__head-name">
            {conversationName || "Conversation"}
          </span>
          {statusText ? (
            <span className="message__head-activity">{statusText}</span>
          ) : null}
        </span>
      </button>

      <div
        className="message__head-actions"
        role="toolbar"
        aria-label="Conversation actions"
      >
        <button
          type="button"
          className="message__head-action"
          title="Search Chat"
          aria-label="Search Chat"
          onClick={toggleSearchmodal}
          disabled={!conversationData}
        >
          <i className="fa fa-search" aria-hidden="true"></i>
        </button>
        <button
          type="button"
          className="message__head-action message__head-action--secondary"
          title="View Files"
          aria-label="View Files"
          onClick={toggleViewConversationFilesModal}
          disabled={!conversationData}
        >
          <i className="fa fa-files-o" aria-hidden="true"></i>
        </button>
        <button
          type="button"
          className="message__head-action message__head-action--secondary"
          title="View Members"
          aria-label="View Members"
          onClick={toggleViewMembersModal}
          disabled={!conversationData}
        >
          <i className="fa fa-users" aria-hidden="true"></i>
        </button>
        <button
          type="button"
          className="message__head-action message__head-action--secondary"
          title="Activity Log"
          aria-label="Activity Log"
          onClick={toggleViewActivityLogModal}
          disabled={!conversationData}
        >
          <i className="fa fa-history" aria-hidden="true"></i>
        </button>

        <div
          className={`message__head-more${
            isSelfAdmin ? "" : " message__head-more--admin-only"
          }`}
          ref={moreMenuRef}
        >
          <button
            type="button"
            className="message__head-action"
            title="More"
            aria-label="More options"
            aria-expanded={isMoreMenuOpen}
            aria-haspopup="menu"
            onClick={toggleDropdown}
            disabled={!conversationData}
          >
            <i className="fa fa-ellipsis-h" aria-hidden="true"></i>
          </button>
          <ul
            className={`message__dropdown ${
              isMoreMenuOpen ? "" : "u-hidden"
            }`}
            role="menu"
          >
            {isSelfAdmin && (
              <li className="message__dropdown-item" role="none">
                <button
                  type="button"
                  className="message__dropdown-link"
                  role="menuitem"
                  onClick={toggleEditConversationModal}
                >
                  <i className="fa fa-cog" aria-hidden="true"></i>
                  Group Settings
                </button>
              </li>
            )}
            <li
              className="message__dropdown-item message__dropdown-item--collapsed"
              role="none"
            >
              <button
                type="button"
                className="message__dropdown-link"
                role="menuitem"
                onClick={toggleViewConversationFilesModal}
              >
                <i className="fa fa-files-o" aria-hidden="true"></i>
                View Files
              </button>
            </li>
            <li
              className="message__dropdown-item message__dropdown-item--collapsed"
              role="none"
            >
              <button
                type="button"
                className="message__dropdown-link"
                role="menuitem"
                onClick={toggleViewMembersModal}
              >
                <i className="fa fa-users" aria-hidden="true"></i>
                View Members
              </button>
            </li>
            <li
              className="message__dropdown-item message__dropdown-item--collapsed"
              role="none"
            >
              <button
                type="button"
                className="message__dropdown-link"
                role="menuitem"
                onClick={toggleViewActivityLogModal}
              >
                <i className="fa fa-history" aria-hidden="true"></i>
                Activity Log
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default MessageHead;
